import { z } from 'zod';

import type { SortOrder, TicketStatus } from '../../types/api/help-hub-api';
import {
  createTicketRequestSchema,
  ticketResponseSchema,
  ticketStatusSchema,
} from '../../types/api/schemas';
import { transformValuesToFrontend } from '../../utils/utils';

// Define all catalog schemas here to avoid circular imports
export const fieldIdSchema = z.string();

// Schema for a single field's metadata (pattern stored as string in JSON, transformed to RegExp)
export const fieldSchema = z.object({
  id: z.string(),
  label: z.string(),
  placeholder: z.string().optional(),
  dataType: z.enum(['number', 'text', 'date']),
  inputType: z.enum(['text', 'textarea', 'select', 'checkbox']),
  isMulti: z.boolean(),
  isRequired: z.boolean(),
  providerKey: z.string(),
  validation: z.object({
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    pattern: z
      .string()
      .transform((s) => new RegExp(s))
      .optional(),
    patternMessage: z.string().optional(),
  }),
  options: z.array(z.object({ id: z.string(), label: z.string() })).optional(),
});

export const fieldsSchema = z.record(z.string(), fieldSchema);

export const nudgeSchema = z.object({
  text: z.string(),
  isRequired: z.boolean(),
  title: z.string().optional(),
});

export const supportChannelSchema = z.object({
  type: z.string(),
  email: z.string().optional(),
  phoneNumber: z.string().optional(),
  serviceName: z.string().optional(),
  url: z.string().optional(),
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fieldOverrideSchema = z.object({
  label: z.string().optional(),
  placeholder: z.string().optional(),
  options: z.array(z.object({ id: z.string(), label: z.string() })).optional(),
  isRequired: z.boolean().optional(),
});

// Raw field reference from the API — key plus optional override properties stored as-is
export const fieldConfigSchema = z.object({
  key: z.string(),
  label: z
    .string()
    .optional()
    .nullable()
    .transform((v) => v ?? undefined),
  placeholder: z
    .string()
    .optional()
    .nullable()
    .transform((v) => v ?? undefined),
  options: z
    .array(z.string())
    .optional()
    .nullable()
    .transform((v) => v ?? undefined),
  isRequired: z
    .boolean()
    .optional()
    .nullable()
    .transform((v) => v ?? undefined),
});

export const categorySchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  supportChannel: supportChannelSchema.optional(),
  fields: z.array(fieldConfigSchema).optional(),
  canEscalate: z.boolean().optional(),
  isFeedback: z.boolean().optional(),
  nudge: nudgeSchema.optional(),
});

export const departmentSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  supportChannel: supportChannelSchema.optional(),
  canEscalate: z.boolean().optional(),
  isFeedback: z.boolean().optional(),
  fields: z.array(fieldConfigSchema).optional(),
  categories: z.array(categorySchema),
  nudge: nudgeSchema.optional(),
});

export const areaSchema = z.object({
  key: z.string(),
  name: z.string(),
  supportChannel: supportChannelSchema.optional(),
  canEscalate: z.boolean().optional(),
  departments: z.array(departmentSchema),
});

export const categoriesSchema = z.array(areaSchema);

export const catalogSchema = z.object({
  categories: categoriesSchema,
  fields: fieldsSchema,
  version: z.string(),
});

export type Area = z.infer<typeof areaSchema>;
export type Department = z.infer<typeof departmentSchema>;
export type Category = z.infer<typeof categorySchema>;
export type FieldOverride = z.infer<typeof fieldOverrideSchema>;
export type FieldConfig = z.infer<typeof fieldConfigSchema>;
export type FieldId = z.infer<typeof fieldIdSchema>;
export type Categories = z.infer<typeof categoriesSchema>;
export type Catalog = z.infer<typeof catalogSchema>;

import type { Field } from './field';
import type { SchemaErrors } from './validation';

export const upcMinLength = 7;

// schemas
export const attachmentSchema = z.object({
  data: z.string(),
  filename: z.string(),
});

export const descriptionSchema = createTicketRequestSchema.shape.description;
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const descriptionMaxLength = descriptionSchema.maxLength!;

export const upcSchema = z
  .string()
  .min(upcMinLength, `Barcode must be at least ${upcMinLength} digits`)
  .regex(/^\d+$/, 'Barcode must contain only digits');

export const upcArraySchema = z
  .array(z.string())
  .superRefine((upcs, ctx) => {
    const result = z.array(upcSchema).safeParse(upcs);
    if (result.success) {
      return;
    }
    const errors = result.error.issues;
    const firstErrorMessage = errors[0].message;

    // Single UPC and it's invalid
    if (upcs.length === 1) {
      ctx.addIssue({
        code: 'custom',
        message: firstErrorMessage,
      });
      return;
    }

    // Multiple UPCs, only one is invalid
    if (errors.length === 1) {
      ctx.addIssue({
        code: 'custom',
        message: `One of your codes is invalid: ${firstErrorMessage}`,
      });
      return;
    }

    // Multiple invalid codes
    const firstError = firstErrorMessage;
    ctx.addIssue({
      code: 'custom',
      message: `You have ${errors.length} invalid codes: ${firstError}`,
    });
  })
  .optional();

export const attachmentArraySchema = z.array(attachmentSchema).optional();

export const isTerminalDepartment = (department: Department | undefined) =>
  !department?.categories.length;

/**
 * A department that owns categories cannot be submitted on its own — one of its
 * own categories has to be selected. Only genuinely terminal departments, which
 * own no categories, may be submitted without one.
 */
export const requiresCategorySelection = (
  department: Department | undefined,
  category: Category | undefined
) =>
  !isTerminalDepartment(department) &&
  !department?.categories.some(({ key }) => key === category?.key);

// validates the relationship between area, department, and category
// note: it differs from categorySchema which validates a single category
export const categoryValidationSchema = z
  .object({
    area: areaSchema.optional(),
    department: departmentSchema.optional(),
    category: categorySchema.optional(),
  })
  .refine((data) => !!data.area?.name, {
    message: 'Area must be selected',
  })
  .refine((data) => !data.category?.name || !!data.department?.name, {
    message: 'Department must be selected if a category is selected',
  })
  .refine(
    (data) => !requiresCategorySelection(data.department, data.category),
    {
      message:
        'A category belonging to the selected department must be selected',
    }
  );

export const issueStatus = ticketStatusSchema.options;

export type IssueStatus = TicketStatus;

export const issueSchema = ticketResponseSchema.transform((api) => ({
  area: api.area,
  category: api.category,
  createdAt: api.created_at,
  department: api.department,
  description: api.description ?? '',
  id: api.id,
  status: api.status,
  storeId: api.store_id?.toString(),
  subject: api.subject,
  createdBy: api.created_by,
  isFeedback: api.is_feedback,
  customFields: transformValuesToFrontend(api.custom_fields),
  resolutionNote: api.resolution_note,
  slas: api.slas?.map((sla) => ({
    id: sla.id,
    dueDate: sla.due_date,
    expired: sla.expired,
  })),
}));

export const draftIssueSchema = createTicketRequestSchema
  .extend({
    attachments: attachmentArraySchema,
    fields: z.array(fieldIdSchema),
    area: areaSchema,
    department: departmentSchema,
    category: categorySchema,
    storeId: z.string(),
  })
  .partial();

//  types inferred from zod schemas
export type Attachment = z.infer<typeof attachmentSchema>;

export type DraftIssue = z.infer<typeof draftIssueSchema>;

export type Issue = z.infer<typeof issueSchema>;

export type IssueFormData = Partial<
  Pick<DraftIssue, 'description' | 'attachments'> & {
    area: string;
    department: string;
    category: string;
    isFeedback: boolean;
    storeId: string;
    customFields: Record<string, string | string[]>;
    // Catalog field metadata used for API value transformation (distinct from DraftIssue.fields which is FieldId[])
    catalogFields: Record<string, Field>;
    catalogVersion: string;
  }
>;

export type DraftIssueValidationResult =
  | { isValid: true; data: z.infer<typeof draftIssueSchema> }
  | { isValid: false; errors: SchemaErrors };

export type GetIssuesDataParams = {
  sort?: SortOrder;
  status?: IssueStatus | 'all';
  area?: string;
  reporter?: string;
  page?: string;
  query?: string;
};
